import { createSelector } from 'reselect';
import {
  selectCurrentUserIsStaff,
  selectCurrentUserEducatorStatus,
  selectCurrentUserLoaded,
} from '../../Users/selectors';
import {
  selectCurrentCourseHasLoaded,
  selectCurrentCourseSubscriptionId,
  selectCurrentCourseSubscriptionPlanType,
  selectCurrentCourseSubscriptionIsActive,
  selectIsAdminOfCurrentCourse,
  currentCourseInstitutionSelector,
} from '../selectors';
import { selectCurrentUserSubscriptionPlanSummaries } from '../../UserSubscriptionPlans/selectors';
import { selectIsFacilitatorInCurrentCourse } from '../../Enrolments/selectors';

type SubscriptionRequiredData = {
  subscriptionRequired: boolean;
  subscriptionId: string | null;
  isLegacyEducator: boolean;
};

export const selectSubscriptionRequired = createSelector(
  selectCurrentUserLoaded,
  selectCurrentUserIsStaff,
  selectCurrentCourseHasLoaded,
  selectIsAdminOfCurrentCourse,
  selectIsFacilitatorInCurrentCourse,
  selectCurrentCourseSubscriptionId,
  selectCurrentUserSubscriptionPlanSummaries,
  selectCurrentUserEducatorStatus,
  selectCurrentCourseSubscriptionPlanType,
  selectCurrentCourseSubscriptionIsActive,
  currentCourseInstitutionSelector,
  (
    currentUserLoaded,
    isOpenLearningStaff,
    courseHasLoaded,
    isCourseAdmin,
    isCourseStaff,
    courseSubscriptionId,
    subscriptionPlans,
    educatorStatus,
    courseSubscriptionPlanType,
    courseSubscriptionIsActive,
    institution
  ): SubscriptionRequiredData | null => {
    if (!currentUserLoaded || !courseHasLoaded) {
      return null; // we don't have enough information yet
    }

    if (isOpenLearningStaff) {
      // never block openlearning staff from accessing a course
      return null;
    }

    if (!(isCourseAdmin || isCourseStaff)) {
      // never block students with subscribe modal
      return null;
    }

    if (courseSubscriptionId && courseSubscriptionPlanType !== 'educator') {
      // this is a subscription course and requires an active subscription
      const activeSubscription = subscriptionPlans.find(
        (plan) =>
          plan.id === courseSubscriptionId &&
          (plan.status === 'trial' ||
            plan.status === 'active' ||
            plan.status === 'cancel_pending' ||
            plan.status === 'past_due')
      );
      if (!activeSubscription) {
        // never block for a course admin or staff if the course subscription is active
        // and is of type portal or custom.
        if (
          (courseSubscriptionPlanType === 'portal' ||
            courseSubscriptionPlanType === 'custom') &&
          courseSubscriptionIsActive
        ) {
          return null;
        }

        // otherwise block with subscribe modal
        // TODO: this needs to be updated to show a different modal for different plan types.
        return {
          subscriptionRequired: true,
          subscriptionId: courseSubscriptionId,
          isLegacyEducator: false,
        };
      }
    } else if (educatorStatus === null && !institution) {
      // this is a course under the legacy "educator" system
      // and the user must be an active educator
      return {
        subscriptionRequired: true,
        subscriptionId: null,
        isLegacyEducator: true,
      };
    }

    return null;
  }
);

declare type CourseAdminType =
  | 'unknown'
  | 'openlearning-staff'
  | 'course-admin'
  | 'educator'
  | 'personal'
  | 'portal'
  | 'custom';

export const selectCourseAdminType = createSelector(
  selectCurrentUserLoaded,
  selectCurrentCourseHasLoaded,
  selectCurrentUserIsStaff,
  selectIsAdminOfCurrentCourse,
  selectCurrentCourseSubscriptionId,
  selectCurrentUserSubscriptionPlanSummaries,
  (
    userLoaded,
    courseLoaded,
    isOpenLearningStaff,
    isCourseAdmin,
    subscriptionId,
    subscriptionPlans
  ): CourseAdminType => {
    if (!(userLoaded && courseLoaded)) {
      return 'unknown'; // we don't have enough info yet
    }

    if (isOpenLearningStaff) {
      return 'openlearning-staff';
    }

    if (!subscriptionId) {
      return 'educator';
    }

    // look for the subscription that matches this current subscription id
    const subscription = subscriptionPlans.find(
      (plan) => plan.id === subscriptionId
    );
    if (
      subscription &&
      (subscription.type === 'personal' ||
        subscription.type === 'portal' ||
        subscription.type === 'custom')
    ) {
      return subscription.type;
    }

    // the course is under a subscription but not owned by this user.
    if (isCourseAdmin) {
      return 'course-admin';
    }

    return 'unknown';
  }
);
